import React from 'react';

/**
 * Helsinki's South Harbour (Eteläsatama) seen from the water, the way you see it from the
 * Suomenlinna ferry: Kaivopuisto's wooded hill and the Olympia terminal on the left, the Old
 * Market Hall and the orange tents of Kauppatori (Market Square) in the middle with the
 * neoclassical row behind them (City Hall, Swedish Embassy, Supreme Court, Presidential Palace),
 * Helsinki Cathedral rising above the rooftops, then Katajanokka on the right: Alvar Aalto's white
 * Enso-Gutzeit "sugar cube", Uspenski Cathedral on its rock, Allas Sea Pool, the SkyWheel and a
 * Viking Line ferry at the Katajanokka terminal.
 *
 * Everything is drawn in a local coordinate system whose origin is the centre of the scene on the
 * waterline: x grows to the right, y is negative going UP (so a rooftop at 40px above the water
 * is y = -40). The scene translates this group to its own centre/waterline. Design width is about
 * 1200 units (-600..600); scenes narrower than that simply crop the outer edges (Kaivopuisto on
 * the left, the Katajanokka apartment blocks on the right), keeping the landmarks centred.
 *
 * All geometry is static and deterministic so server and client render byte-identical markup.
 */

// Palette. Deliberately a touch desaturated: this is a backdrop, the icebergs are the subject.
const C = {
  stoneWhite: '#f4f3ec',
  stoneShade: '#dfded4',
  stoneLine: '#c9c8bd',
  patina: '#5c9478',
  patinaShade: '#457a5f',
  gold: '#d6ab45',
  goldLight: '#f1d283',
  brick: '#8f3f2e',
  brickShade: '#70301f',
  brickDark: '#4a2318',
  granite: '#7b7566',
  graniteShade: '#615c50',
  roofDark: '#4d545c',
  roofGrey: '#8c98a1',
  windowDark: '#3a4550',
  windowGlass: '#8fb4c9',
  cityHallBlue: '#c8dfeb',
  cream: '#efe3bc',
  paleYellow: '#f3e3ab',
  pink: '#e6cdc0',
  marketOrange: '#e98a2f',
  marketOrangeShade: '#c9711f',
  wood: '#c0965d',
  woodShade: '#9c7645',
  hullRed: '#c4202e',
  hullRedShade: '#9a1723',
  shipWhite: '#f7f7f3',
  shipBlue: '#1f4f9e',
  treeDark: '#2f5a42',
  treeMid: '#3d7354',
  treeLight: '#5a8f66',
  hillGreen: '#6a8c6c',
  wheelWhite: '#f2f4f5',
  wheelGrey: '#b9c3ca',
  flagBlue: '#1b4c9d',
} as const;

function n(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

/** A grid of small window rectangles. */
function Windows({
  x,
  y,
  cols,
  rows,
  w,
  h,
  stepX,
  stepY,
  fill = C.windowDark,
  opacity = 1,
}: {
  x: number;
  y: number;
  cols: number;
  rows: number;
  w: number;
  h: number;
  stepX: number;
  stepY: number;
  fill?: string;
  opacity?: number;
}) {
  const rects: React.ReactNode[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      rects.push(
        <rect
          key={`${r}-${c}`}
          className={fill === C.windowDark ? 'hki-win' : fill === C.windowGlass ? 'hki-glass' : undefined}
          x={n(x + c * stepX)}
          y={n(y + r * stepY)}
          width={n(w)}
          height={n(h)}
          fill={fill}
          opacity={opacity}
        />,
      );
    }
  }
  return <>{rects}</>;
}

/** A round-topped (arched) window or doorway. `y` is the bottom edge, height grows upward. */
function Arch({ x, y, w, h, fill = C.windowDark }: { x: number; y: number; w: number; h: number; fill?: string }) {
  const r = w / 2;
  return <path className={fill === C.brickDark ? 'hki-arch' : undefined} d={`M${n(x)},${n(y)} V${n(y - h + r)} A${n(r)},${n(r)} 0 0 1 ${n(x + w)},${n(y - h + r)} V${n(y)} Z`} fill={fill} />;
}

/** Row of classical columns between `y1` (bottom) and `y2` (top). */
function Columns({ x, count, step, w, y1, y2, fill = C.stoneWhite, shade = C.stoneLine }: { x: number; count: number; step: number; w: number; y1: number; y2: number; fill?: string; shade?: string }) {
  const out: React.ReactNode[] = [];
  for (let i = 0; i < count; i++) {
    const cx = x + i * step;
    out.push(
      <g key={i}>
        <rect x={n(cx)} y={n(y2)} width={n(w)} height={n(y1 - y2)} fill={fill} />
        <rect x={n(cx + w * 0.62)} y={n(y2)} width={n(w * 0.38)} height={n(y1 - y2)} fill={shade} opacity={0.7} />
        <rect x={n(cx - 0.6)} y={n(y2 - 1.2)} width={n(w + 1.2)} height={1.2} fill={fill} />
        <rect x={n(cx - 0.6)} y={n(y1 - 1)} width={n(w + 1.2)} height={1} fill={shade} />
      </g>,
    );
  }
  return <>{out}</>;
}

/** Onion dome (Uspenski style): bulges wider than its drum, then tapers to a point with a cross. */
function OnionDome({ cx, baseY, w, h, fill = C.gold, light = C.goldLight, cross = 6 }: { cx: number; baseY: number; w: number; h: number; fill?: string; light?: string; cross?: number }) {
  const l = cx - w * 0.4;
  const r = cx + w * 0.4;
  const top = baseY - h;
  const d = `M${n(l)},${n(baseY)} C${n(cx - w * 0.72)},${n(baseY - h * 0.22)} ${n(cx - w * 0.52)},${n(baseY - h * 0.62)} ${n(cx)},${n(top)} C${n(cx + w * 0.52)},${n(baseY - h * 0.62)} ${n(cx + w * 0.72)},${n(baseY - h * 0.22)} ${n(r)},${n(baseY)} Z`;
  return (
    <g>
      <path d={d} fill={fill} />
      <path
        d={`M${n(cx - w * 0.18)},${n(baseY - 1)} C${n(cx - w * 0.5)},${n(baseY - h * 0.25)} ${n(cx - w * 0.36)},${n(baseY - h * 0.6)} ${n(cx - w * 0.04)},${n(top + h * 0.06)}`}
        fill="none"
        stroke={light}
        strokeWidth={1.4}
        strokeLinecap="round"
        opacity={0.9}
      />
      <line x1={n(cx)} y1={n(top)} x2={n(cx)} y2={n(top - cross)} stroke={fill} strokeWidth={1.2} />
      <line x1={n(cx - cross * 0.35)} y1={n(top - cross * 0.62)} x2={n(cx + cross * 0.35)} y2={n(top - cross * 0.62)} stroke={fill} strokeWidth={1.2} />
    </g>
  );
}

/** Green copper dome (Helsinki Cathedral style): a smooth half-ellipse on a drum. */
function CopperDome({ cx, baseY, rx, ry, lantern = true }: { cx: number; baseY: number; rx: number; ry: number; lantern?: boolean }) {
  const top = baseY - ry;
  return (
    <g>
      <path d={`M${n(cx - rx)},${n(baseY)} A${n(rx)},${n(ry)} 0 0 1 ${n(cx + rx)},${n(baseY)} Z`} fill={C.patina} />
      {/* shadow side */}
      <path d={`M${n(cx + rx * 0.15)},${n(baseY)} A${n(rx * 0.85)},${n(ry)} 0 0 1 ${n(cx + rx)},${n(baseY)} Z`} fill={C.patinaShade} opacity={0.75} />
      {/* ribs */}
      {[-0.55, -0.2, 0.2, 0.55].map((f) => (
        <path
          key={f}
          d={`M${n(cx + rx * f)},${n(baseY)} Q${n(cx + rx * f * 0.8)},${n(baseY - ry * 0.9)} ${n(cx)},${n(top)}`}
          fill="none"
          stroke={C.patinaShade}
          strokeWidth={0.6}
          opacity={0.55}
        />
      ))}
      {lantern && (
        <g>
          <rect x={n(cx - 3.5)} y={n(top - 9)} width={7} height={9.5} fill={C.stoneWhite} />
          <rect x={n(cx + 0.8)} y={n(top - 9)} width={2.7} height={9.5} fill={C.stoneShade} />
          <path d={`M${n(cx - 4.2)},${n(top - 9)} A4.2,4 0 0 1 ${n(cx + 4.2)},${n(top - 9)} Z`} fill={C.patina} />
          <line x1={n(cx)} y1={n(top - 13)} x2={n(cx)} y2={n(top - 21)} stroke={C.gold} strokeWidth={1.2} />
          <line x1={n(cx - 2.2)} y1={n(top - 18)} x2={n(cx + 2.2)} y2={n(top - 18)} stroke={C.gold} strokeWidth={1.2} />
        </g>
      )}
    </g>
  );
}

function Conifer({ x, y, h, fill = C.treeDark }: { x: number; y: number; h: number; fill?: string }) {
  const w = h * 0.55;
  return (
    <g>
      <polygon points={`${n(x)},${n(y - h)} ${n(x - w / 2)},${n(y)} ${n(x + w / 2)},${n(y)}`} fill={fill} />
      <polygon points={`${n(x)},${n(y - h)} ${n(x - w / 2)},${n(y)} ${n(x)},${n(y)}`} fill="#ffffff" opacity={0.08} />
    </g>
  );
}

function RoundTree({ x, y, r, fill = C.treeMid }: { x: number; y: number; r: number; fill?: string }) {
  return (
    <g>
      <rect x={n(x - 0.9)} y={n(y - r * 0.6)} width={1.8} height={n(r * 0.6)} fill={C.brickDark} />
      <circle cx={n(x)} cy={n(y - r * 1.1)} r={n(r)} fill={fill} />
      <circle cx={n(x - r * 0.35)} cy={n(y - r * 1.35)} r={n(r * 0.55)} fill="#ffffff" opacity={0.1} />
    </g>
  );
}

function FinnishFlag({ x, y, h }: { x: number; y: number; h: number }) {
  const fw = 11;
  const fh = 7;
  return (
    <g>
      <line x1={n(x)} y1={n(y)} x2={n(x)} y2={n(y - h)} stroke={C.roofGrey} strokeWidth={1} />
      <rect x={n(x + 0.5)} y={n(y - h)} width={fw} height={fh} fill="#ffffff" stroke={C.stoneLine} strokeWidth={0.4} />
      <rect x={n(x + 0.5 + fw * 0.3)} y={n(y - h)} width={n(fw * 0.16)} height={fh} fill={C.flagBlue} />
      <rect x={n(x + 0.5)} y={n(y - h + fh * 0.4)} width={fw} height={n(fh * 0.22)} fill={C.flagBlue} />
    </g>
  );
}

/* ---------------------------------------------------------------------------------------------- */

function KaivopuistoHill() {
  // Wooded hill at the far left with the little Ursa observatory on top and a couple of the
  // old embassy villas peeking through the trees.
  const ridge = '-620,0 -600,-8 -560,-18 -520,-24 -480,-27 -440,-26 -400,-20 -370,-12 -345,-5 -332,0';
  return (
    <g>
      <polygon points={ridge} fill={C.hillGreen} />
      <polygon points="-620,0 -600,-8 -560,-18 -520,-24 -480,-27 -440,-26 -400,-20 -370,-12 -345,-5 -332,0" fill="#000000" opacity={0.06} />
      {/* villas */}
      <rect x={-566} y={-30} width={18} height={12} fill={C.cream} />
      <polygon points="-568,-30 -557,-37 -546,-30" fill={C.roofDark} />
      <rect x={-408} y={-27} width={16} height={9} fill={C.paleYellow} />
      <polygon points="-410,-27 -400,-33 -390,-27" fill={C.roofDark} />
      {/* Ursa observatory */}
      <rect x={-478} y={-40} width={14} height={14} fill={C.stoneWhite} />
      <rect x={-469} y={-40} width={5} height={14} fill={C.stoneShade} />
      <path d="M-479,-40 A8,7.5 0 0 1 -463,-40 Z" fill="#c9d1d6" />
      <path d="M-471,-47.5 A8,7.5 0 0 1 -463,-40 L-471,-40 Z" fill="#a9b4bb" />
      {/* trees along the ridge */}
      <Conifer x={-598} y={-6} h={13} />
      <RoundTree x={-585} y={-12} r={6} />
      <Conifer x={-572} y={-15} h={15} fill={C.treeMid} />
      <Conifer x={-540} y={-20} h={18} />
      <RoundTree x={-526} y={-22} r={7} fill={C.treeLight} />
      <Conifer x={-508} y={-24} h={16} fill={C.treeMid} />
      <RoundTree x={-494} y={-25} r={6} />
      <Conifer x={-452} y={-25} h={19} />
      <RoundTree x={-436} y={-25} r={7} fill={C.treeLight} />
      <Conifer x={-422} y={-22} h={15} fill={C.treeMid} />
      <RoundTree x={-384} y={-16} r={6} />
      <Conifer x={-364} y={-11} h={14} />
      <RoundTree x={-350} y={-6} r={5} fill={C.treeLight} />
    </g>
  );
}

function OlympiaTerminalAndSiljaFerry() {
  return (
    <g>
      {/* Olympia terminal: long low white 1950s building with a clock tower */}
      <rect x={-334} y={-20} width={84} height={20} fill={C.stoneWhite} />
      <rect x={-334} y={-15} width={84} height={5} fill={C.windowGlass} opacity={0.8} />
      <rect x={-334} y={-22} width={84} height={2} fill={C.roofGrey} />
      <rect x={-304} y={-36} width={9} height={16} fill={C.stoneWhite} />
      <rect x={-298} y={-36} width={3} height={16} fill={C.stoneShade} />
      <circle cx={-299.5} cy={-31} r={2.2} fill="#ffffff" stroke={C.windowDark} strokeWidth={0.6} />
      {/* Silja Line cruise ferry docked alongside: white hull, blue stripe, tall superstructure */}
      <polygon points="-386,-3 -378,-15 -252,-15 -252,1 -380,1" fill={C.shipWhite} />
      <polygon points="-386,-3 -380,1 -252,1 -252,-3" fill={C.stoneShade} />
      <rect x={-378} y={-12} width={126} height={2.6} fill={C.shipBlue} />
      <Windows x={-370} y={-9} cols={22} rows={1} w={2} h={1.6} stepX={5.4} stepY={0} fill={C.windowDark} opacity={0.6} />
      <polygon points="-370,-15 -366,-46 -258,-46 -256,-15" fill={C.shipWhite} />
      <polygon points="-296,-15 -294,-46 -258,-46 -256,-15" fill={C.stoneShade} opacity={0.35} />
      {[-42, -35, -28, -21].map((y) => (
        <rect key={y} x={-362} y={y} width={100} height={2.4} fill={C.windowGlass} opacity={0.85} />
      ))}
      <rect x={-356} y={-54} width={84} height={8} fill={C.shipWhite} />
      <rect x={-356} y={-52} width={84} height={2} fill={C.windowGlass} opacity={0.7} />
      {/* funnel with the Silja seal */}
      <rect x={-306} y={-70} width={16} height={16} fill={C.shipWhite} />
      <rect x={-306} y={-72} width={16} height={2.5} fill={C.shipBlue} />
      <circle cx={-298} cy={-62} r={4.2} fill={C.shipBlue} />
      <circle cx={-298} cy={-62} r={2} fill="#ffffff" />
      <line x1={-342} y1={-54} x2={-342} y2={-78} stroke={C.roofGrey} strokeWidth={1} />
      <line x1={-266} y1={-54} x2={-266} y2={-66} stroke={C.roofGrey} strokeWidth={1} />
    </g>
  );
}

function OldMarketHall() {
  // Vanha kauppahalli (1889): long red-brick hall with cream stripes and squat towers at the ends.
  const x0 = -248;
  const w = 100;
  return (
    <g>
      <rect x={x0} y={-27} width={w} height={27} fill={C.brick} />
      <rect x={x0} y={-31} width={w} height={4} fill={C.roofDark} />
      {[-9, -17, -25].map((y) => (
        <rect key={y} x={x0} y={y} width={w} height={1.4} fill={C.cream} opacity={0.9} />
      ))}
      {Array.from({ length: 9 }, (_, i) => (
        <Arch key={i} x={x0 + 8 + i * 10.5} y={-2} w={5} h={13} fill={C.brickDark} />
      ))}
      {/* raised central section with a small cupola */}
      <rect x={x0 + 36} y={-37} width={28} height={10} fill={C.brick} />
      <rect x={x0 + 36} y={-39} width={28} height={2.4} fill={C.roofDark} />
      <rect x={x0 + 36} y={-34} width={28} height={1.4} fill={C.cream} />
      <rect x={x0 + 47} y={-45} width={6} height={6} fill={C.cream} />
      <polygon points={`${x0 + 46},-45 ${x0 + 50},-50 ${x0 + 54},-45`} fill={C.roofDark} />
      {/* end towers */}
      {[x0 - 2, x0 + w - 12].map((tx) => (
        <g key={tx}>
          <rect x={tx} y={-37} width={14} height={37} fill={C.brickShade} />
          <rect x={tx} y={-30} width={14} height={1.4} fill={C.cream} />
          <rect x={tx} y={-20} width={14} height={1.4} fill={C.cream} />
          <Arch x={tx + 4.5} y={-3} w={5} h={11} fill={C.brickDark} />
          <polygon points={`${tx - 1},-37 ${tx + 7},-46 ${tx + 15},-37`} fill={C.roofDark} />
        </g>
      ))}
    </g>
  );
}

function NeoclassicalRow() {
  // The row behind Market Square, left to right: City Hall (Engel, light blue), Swedish Embassy,
  // Supreme Court, Presidential Palace. All Empire-style, all a slightly different pastel.
  return (
    <g>
      {/* City Hall */}
      <rect x={-142} y={-40} width={62} height={40} fill={C.cityHallBlue} />
      <rect x={-142} y={-42} width={62} height={2} fill={C.roofGrey} />
      <Columns x={-138} count={8} step={8} w={2.4} y1={-8} y2={-33} fill="#ffffff" shade={C.stoneLine} />
      <Windows x={-134.5} y={-30} cols={7} rows={2} w={2.6} h={5} stepX={8} stepY={9} fill={C.windowDark} opacity={0.85} />
      {Array.from({ length: 6 }, (_, i) => (
        <Arch key={i} x={-136 + i * 10} y={0} w={4.6} h={7.5} fill={C.windowDark} />
      ))}
      <polygon points="-121,-42 -101,-42 -111,-49" fill={C.cityHallBlue} stroke={C.stoneLine} strokeWidth={0.6} />
      {/* Swedish Embassy */}
      <rect x={-76} y={-38} width={36} height={38} fill={C.cream} />
      <rect x={-76} y={-40} width={36} height={2} fill={C.roofGrey} />
      <Columns x={-72} count={4} step={9} w={2.2} y1={-8} y2={-31} fill="#fbf6e6" shade={C.stoneLine} />
      <Windows x={-68} y={-28} cols={3} rows={2} w={3} h={5} stepX={9} stepY={9} opacity={0.85} />
      <Windows x={-72} y={-6} cols={4} rows={1} w={3} h={4.5} stepX={9} stepY={0} opacity={0.7} />
      {/* Supreme Court */}
      <rect x={-37} y={-36} width={36} height={36} fill={C.pink} />
      <rect x={-37} y={-38} width={36} height={2} fill={C.roofGrey} />
      <Windows x={-33} y={-31} cols={4} rows={3} w={3} h={5} stepX={8.4} stepY={9} opacity={0.85} />
      {/* Presidential Palace */}
      <rect x={2} y={-42} width={86} height={42} fill={C.paleYellow} />
      <rect x={2} y={-44} width={86} height={2} fill={C.roofGrey} />
      {/* rooftop balustrade */}
      {Array.from({ length: 15 }, (_, i) => (
        <rect key={i} x={4 + i * 5.8} y={-47} width={1.4} height={3} fill={C.stoneShade} />
      ))}
      <rect x={2} y={-47.6} width={86} height={0.9} fill={C.stoneShade} />
      <Columns x={30} count={5} step={7.5} w={2.2} y1={-8} y2={-34} fill="#fff8db" shade={C.stoneLine} />
      <Windows x={6} y={-36} cols={3} rows={3} w={3} h={5.2} stepX={7.5} stepY={9.6} opacity={0.85} />
      <Windows x={66} y={-36} cols={3} rows={3} w={3} h={5.2} stepX={7.5} stepY={9.6} opacity={0.85} />
      <Windows x={33.5} y={-31} cols={4} rows={2} w={2.8} h={5.2} stepX={7.5} stepY={9.6} opacity={0.85} />
      <FinnishFlag x={45} y={-48} h={20} />
    </g>
  );
}

function HelsinkiCathedral() {
  // Tuomiokirkko: rises above the rooftops on Senate Square. Base hidden behind the Palace row.
  const cx = -118;
  const base = -40;
  return (
    <g>
      {/* main block */}
      <rect x={cx - 62} y={base - 62} width={124} height={62} fill={C.stoneWhite} />
      <rect x={cx + 22} y={base - 62} width={40} height={62} fill={C.stoneShade} />
      <rect x={cx - 62} y={base - 66} width={124} height={4} fill={C.roofGrey} />
      {/* apostle statues along the roofline */}
      {[-58, -50, -42, 42, 50, 58].map((dx) => (
        <rect key={dx} x={cx + dx - 1.2} y={base - 73} width={2.4} height={7} fill="#b3bcc2" />
      ))}
      {/* side windows */}
      <Windows x={cx - 58} y={base - 52} cols={2} rows={2} w={3} h={8} stepX={8} stepY={14} fill={C.windowDark} opacity={0.5} />
      <Windows x={cx + 44} y={base - 52} cols={2} rows={2} w={3} h={8} stepX={8} stepY={14} fill={C.windowDark} opacity={0.5} />
      {/* portico: six columns and pediment */}
      <rect x={cx - 44} y={base - 60} width={88} height={60} fill="#fbfbf6" />
      <Columns x={cx - 40} count={6} step={15.2} w={5} y1={base - 4} y2={base - 58} fill="#ffffff" shade={C.stoneLine} />
      <rect x={cx - 46} y={base - 64} width={92} height={5} fill={C.stoneWhite} stroke={C.stoneLine} strokeWidth={0.5} />
      <polygon points={`${cx - 47},${base - 69} ${cx + 47},${base - 69} ${cx},${base - 86}`} fill={C.stoneWhite} stroke={C.stoneLine} strokeWidth={0.7} />
      <polygon points={`${cx - 40},${base - 70.5} ${cx + 40},${base - 70.5} ${cx},${base - 84}`} fill={C.stoneShade} opacity={0.35} />
      {/* four corner towers with small green domes: back pair first, then front pair */}
      {[
        [cx - 46, 6],
        [cx + 46, 6],
        [cx - 56, 0],
        [cx + 56, 0],
      ].map(([tx, lift], i) => (
        <g key={i}>
          <rect x={tx - 8} y={base - 84 - lift} width={16} height={18 + lift} fill={i % 2 ? C.stoneShade : C.stoneWhite} />
          <rect x={tx + 2} y={base - 84 - lift} width={6} height={18 + lift} fill={C.stoneShade} />
          <Arch x={tx - 2.2} y={base - 70 - lift} w={4.4} h={9} fill={C.windowDark} />
          <CopperDome cx={tx} baseY={base - 85 - lift} rx={9} ry={9} lantern={false} />
          <line x1={tx} y1={base - 94 - lift} x2={tx} y2={base - 101 - lift} stroke={C.gold} strokeWidth={1} />
          <line x1={tx - 1.8} y1={base - 99 - lift} x2={tx + 1.8} y2={base - 99 - lift} stroke={C.gold} strokeWidth={1} />
        </g>
      ))}
      {/* drum with colonnade */}
      <rect x={cx - 27} y={base - 112} width={54} height={46} fill={C.stoneWhite} />
      <rect x={cx + 9} y={base - 112} width={18} height={46} fill={C.stoneShade} />
      <Columns x={cx - 25} count={9} step={6.4} w={2.4} y1={base - 68} y2={base - 108} fill="#ffffff" shade={C.stoneLine} />
      <rect x={cx - 28} y={base - 114} width={56} height={3} fill={C.stoneWhite} stroke={C.stoneLine} strokeWidth={0.5} />
      {/* great dome */}
      <CopperDome cx={cx} baseY={base - 114} rx={32} ry={34} />
    </g>
  );
}

function MarketSquare() {
  // Kauppatori: the orange market tents at the quay, Havis Amanda at the west end, and the
  // Keisarinnankivi obelisk (the Empress Stone) with its gilded globe.
  const tents = [-134, -112, -90, -68, -46, -24, -2, 20, 42, 64];
  return (
    <g>
      {/* granite quay */}
      <rect x={-150} y={-2.5} width={245} height={2.5} fill={C.granite} />
      {tents.map((tx) => (
        <g key={tx}>
          <polygon points={`${tx},-2 ${tx + 19},-2 ${tx + 19},-8 ${tx + 9.5},-13 ${tx},-8`} fill={C.marketOrange} />
          <polygon points={`${tx + 9.5},-13 ${tx + 19},-8 ${tx + 19},-2 ${tx + 9.5},-2`} fill={C.marketOrangeShade} opacity={0.55} />
          <rect x={tx + 8.5} y={-8} width={2} height={6} fill={C.marketOrangeShade} opacity={0.6} />
        </g>
      ))}
      {/* Havis Amanda */}
      <ellipse cx={-138} cy={-2} rx={7} ry={2.2} fill={C.granite} />
      <rect x={-139.5} y={-13} width={3} height={11} fill={C.graniteShade} />
      <circle cx={-138} cy={-15} r={2} fill="#7c8f96" />
      {/* Keisarinnankivi obelisk */}
      <rect x={78} y={-7} width={12} height={7} fill={C.graniteShade} />
      <polygon points="80,-7 88,-7 86.2,-50 81.8,-50" fill="#a09d94" />
      <polygon points="84,-7 88,-7 86.2,-50 84,-50" fill="#7f7c73" />
      <circle cx={84} cy={-53} r={3} fill={C.gold} />
      <path d="M81.5,-56.5 l2.5,-2.5 l2.5,2.5 l-1.4,1.4 l-1.1,-0.9 l-1.1,0.9 Z" fill={C.goldLight} />
    </g>
  );
}

function EnsoGutzeitCube() {
  // Alvar Aalto's white Carrara-marble office block on the Katajanokka side of the harbour.
  return (
    <g>
      <rect x={96} y={-48} width={60} height={48} fill={C.stoneWhite} />
      <rect x={96} y={-49.5} width={60} height={1.5} fill={C.roofGrey} />
      <rect x={140} y={-48} width={16} height={48} fill={C.stoneShade} opacity={0.6} />
      <Windows x={100} y={-44} cols={6} rows={5} w={6} h={5.4} stepX={9.4} stepY={8.6} fill={C.windowGlass} />
      <Windows x={100} y={-44} cols={6} rows={5} w={6} h={1.4} stepX={9.4} stepY={8.6} fill={C.windowDark} opacity={0.35} />
    </g>
  );
}

function UspenskiCathedral() {
  // Uspenski: red brick on a granite outcrop, thirteen gilded onion cupolas, tall bell tower on
  // the western (left) side. The largest Orthodox church in western Europe.
  const cx = 200;
  return (
    <g>
      {/* rock and the trees on it */}
      <polygon points="124,0 140,-12 168,-24 200,-30 236,-28 262,-18 284,-8 296,0" fill={C.granite} />
      <polygon points="124,0 140,-12 168,-24 200,-30 236,-28 262,-18 284,-8 296,0 270,0" fill={C.graniteShade} opacity={0.5} />
      <RoundTree x={134} y={-8} r={4.5} fill={C.treeDark} />
      <Conifer x={272} y={-13} h={12} />
      <RoundTree x={286} y={-5} r={4} fill={C.treeMid} />
      {/* terrace */}
      <rect x={150} y={-38} width={100} height={8} fill={C.graniteShade} />
      {/* bell tower (west, left) */}
      <rect x={140} y={-104} width={20} height={70} fill={C.brick} />
      <rect x={154} y={-104} width={6} height={70} fill={C.brickShade} />
      <Arch x={147} y={-44} w={6} h={12} fill={C.brickDark} />
      <Arch x={147} y={-64} w={6} h={12} fill={C.brickDark} />
      <Arch x={146} y={-84} w={8} h={14} fill={C.brickDark} />
      <rect x={139} y={-106} width={22} height={2.5} fill={C.brickShade} />
      <OnionDome cx={150} baseY={-106} w={24} h={26} cross={9} />
      {/* main body */}
      <rect x={160} y={-88} width={76} height={50} fill={C.brick} />
      <rect x={214} y={-88} width={22} height={50} fill={C.brickShade} />
      <rect x={160} y={-92} width={76} height={4} fill={C.roofDark} />
      {[166, 182, 200, 218].map((wx) => (
        <Arch key={wx} x={wx} y={-46} w={7} h={22} fill={C.brickDark} />
      ))}
      {[166, 182, 200, 218].map((wx) => (
        <Arch key={wx} x={wx + 1.5} y={-47} w={4} h={19} fill={C.windowGlass} opacity={0.35} />
      ))}
      {/* small cupolas on the roof corners */}
      {[
        [169, -92],
        [227, -92],
        [180, -96],
        [216, -96],
      ].map(([sx, sy], i) => (
        <g key={i}>
          <rect x={sx - 4.5} y={sy - 11} width={9} height={11} fill={i > 1 ? C.brickShade : C.brick} />
          <Arch x={sx - 1.5} y={sy - 2} w={3} h={6} fill={C.brickDark} />
          <OnionDome cx={sx} baseY={sy - 11} w={13} h={11} cross={4} />
        </g>
      ))}
      {/* central drum and the great gilded dome */}
      <rect x={cx - 19} y={-126} width={38} height={34} fill={C.brick} />
      <rect x={cx + 7} y={-126} width={12} height={34} fill={C.brickShade} />
      {[-13, -3, 7].map((dx) => (
        <Arch key={dx} x={cx + dx} y={-98} w={5} h={16} fill={C.brickDark} />
      ))}
      <rect x={cx - 21} y={-129} width={42} height={3} fill={C.roofDark} />
      <OnionDome cx={cx} baseY={-129} w={44} h={38} cross={11} />
    </g>
  );
}

function AllasSeaPool() {
  return (
    <g>
      <rect x={236} y={-3} width={70} height={3} fill={C.wood} />
      <rect x={236} y={-1.2} width={70} height={1.2} fill={C.woodShade} />
      {/* pool building and the sauna: dark wooden boxes */}
      <rect x={242} y={-18} width={30} height={15} fill={C.woodShade} />
      <rect x={242} y={-19.5} width={30} height={1.5} fill={C.roofDark} />
      {Array.from({ length: 7 }, (_, i) => (
        <rect key={i} x={244 + i * 4.2} y={-16} width={1.2} height={12} fill={C.wood} opacity={0.5} />
      ))}
      <rect x={278} y={-12} width={22} height={9} fill={C.wood} />
      <rect x={278} y={-13.5} width={22} height={1.5} fill={C.roofDark} />
      {/* the pool itself, a strip of brighter water on the deck */}
      <rect x={244} y={-3} width={54} height={1.6} fill="#79c8dc" />
    </g>
  );
}

function KatajanokkaTerminalAndVikingFerry() {
  return (
    <g>
      {/* terminal shed */}
      <rect x={372} y={-22} width={70} height={22} fill="#d8d3c6" />
      <rect x={372} y={-24} width={70} height={2} fill={C.roofDark} />
      {/* Viking Line ferry, bow to the right */}
      <polygon points="382,1 382,-18 522,-18 536,-9 534,1" fill={C.hullRed} />
      <polygon points="382,1 382,-4 534,-4 534,1" fill={C.hullRedShade} />
      <rect x={390} y={-14} width={130} height={1.6} fill="#ffffff" opacity={0.85} />
      <polygon points="390,-18 392,-50 512,-50 520,-18" fill={C.shipWhite} />
      <polygon points="486,-18 488,-50 512,-50 520,-18" fill={C.stoneShade} opacity={0.4} />
      {[-46, -39, -32, -25].map((y) => (
        <rect key={y} x={398} y={y} width={112} height={2.4} fill={C.windowGlass} opacity={0.85} />
      ))}
      <rect x={402} y={-58} width={96} height={8} fill={C.shipWhite} />
      <rect x={402} y={-56} width={96} height={2} fill={C.windowGlass} opacity={0.7} />
      <rect x={462} y={-74} width={14} height={16} fill={C.hullRed} />
      <rect x={462} y={-76} width={14} height={2.5} fill={C.hullRedShade} />
      <line x1={414} y1={-58} x2={414} y2={-80} stroke={C.roofGrey} strokeWidth={1} />
      <FinnishFlag x={530} y={-18} h={14} />
    </g>
  );
}

function KatajanokkaBlocks() {
  // The Jugendstil apartment blocks along Katajanokka's shore and the old red-brick prison
  // (now Hotel Katajanokka) with its water tower.
  return (
    <g>
      <rect x={540} y={-40} width={32} height={40} fill={C.cream} />
      <rect x={540} y={-42} width={32} height={2} fill={C.roofDark} />
      <Windows x={543} y={-36} cols={3} rows={4} w={3} h={5} stepX={9.5} stepY={9} opacity={0.8} />
      <rect x={542} y={-54} width={10} height={14} fill={C.cream} />
      <polygon points="541,-54 547,-64 553,-54" fill={C.roofDark} />
      <rect x={576} y={-34} width={30} height={34} fill={C.pink} />
      <rect x={576} y={-36} width={30} height={2} fill={C.roofDark} />
      <Windows x={579} y={-30} cols={3} rows={3} w={3} h={5} stepX={9.5} stepY={9} opacity={0.8} />
      <rect x={610} y={-30} width={40} height={30} fill={C.brick} />
      <rect x={610} y={-32} width={40} height={2} fill={C.roofDark} />
      <Windows x={614} y={-26} cols={4} rows={3} w={3} h={5} stepX={9} stepY={8} fill={C.brickDark} opacity={0.9} />
      <rect x={628} y={-48} width={12} height={18} fill={C.brickShade} />
      <polygon points="626,-48 634,-56 642,-48" fill={C.roofDark} />
    </g>
  );
}

function SkyWheel({ animPrefix }: { animPrefix?: string }) {
  const cx = 340;
  const cy = -46;
  const r = 36;
  const spokes = 12;
  const rim = Array.from({ length: spokes }, (_, i) => {
    const a = (i / spokes) * Math.PI * 2;
    return [Math.cos(a) * r, Math.sin(a) * r] as const;
  });
  return (
    <g>
      <line x1={cx - 22} y1={0} x2={cx} y2={cy} stroke={C.wheelGrey} strokeWidth={2.2} />
      <line x1={cx + 22} y1={0} x2={cx} y2={cy} stroke={C.wheelGrey} strokeWidth={2.2} />
      <rect x={cx - 26} y={-3} width={52} height={3} fill={C.granite} />
      {/* The wheel is drawn around its own origin so a CSS rotate() spins it in place. */}
      <g transform={`translate(${cx},${cy})`}>
        <g className={animPrefix ? `${animPrefix}-spin` : undefined}>
          <circle cx={0} cy={0} r={r} fill="none" stroke={C.wheelWhite} strokeWidth={2} />
          <circle cx={0} cy={0} r={r - 4} fill="none" stroke={C.wheelWhite} strokeWidth={0.8} opacity={0.8} />
          {rim.map(([px, py], i) => (
            <line key={i} x1={0} y1={0} x2={n(px)} y2={n(py)} stroke={C.wheelWhite} strokeWidth={0.9} />
          ))}
          {rim.map(([px, py], i) => (
            <rect key={i} x={n(px - 3)} y={n(py - 1)} width={6} height={6.5} rx={1.6} fill={i % 3 === 0 ? C.windowGlass : C.wheelWhite} stroke={C.wheelGrey} strokeWidth={0.5} />
          ))}
          <circle cx={0} cy={0} r={4} fill={C.wheelGrey} />
        </g>
      </g>
    </g>
  );
}

const COAT_COLOURS = ['#2b3f6b', '#c4443a', '#e8b23a', '#3f6b48', '#333a40', '#7a4b8a', '#d9d4c7'];

/**
 * Tiny figures strolling the quays: a head and a coat, about three units tall, so at scene scale
 * they read as people in the distance. Each wanders back and forth along its stretch of quay
 * (`walk` is the distance, in scene units; negative walks the other way first).
 */
function Strollers({ animPrefix }: { animPrefix?: string }) {
  const people: Array<{ x: number; y: number; walk: number; duration: number; delay: number; coat: number }> = [
    // Kauppatori quay, in front of the market tents
    { x: -128, y: 0, walk: 46, duration: 34, delay: -6, coat: 0 },
    { x: -96, y: 0, walk: -38, duration: 27, delay: -13, coat: 1 },
    { x: -70, y: 0, walk: 52, duration: 41, delay: -2, coat: 2 },
    { x: -34, y: 0, walk: -30, duration: 23, delay: -17, coat: 3 },
    { x: -8, y: 0, walk: 44, duration: 36, delay: -9, coat: 4 },
    { x: 26, y: 0, walk: -48, duration: 44, delay: -25, coat: 5 },
    { x: 56, y: 0, walk: 24, duration: 19, delay: -4, coat: 6 },
    // Olympia terminal quay
    { x: -322, y: 0, walk: 30, duration: 29, delay: -8, coat: 1 },
    { x: -284, y: 0, walk: -26, duration: 24, delay: -15, coat: 3 },
    // Allas Sea Pool decks
    { x: 240, y: -3, walk: 28, duration: 22, delay: -3, coat: 6 },
    { x: 262, y: -3, walk: -22, duration: 26, delay: -11, coat: 2 },
    { x: 288, y: -3, walk: 14, duration: 17, delay: -7, coat: 0 },
    // Katajanokka quay by the wheel
    { x: 318, y: 0, walk: 40, duration: 33, delay: -20, coat: 4 },
    { x: 362, y: 0, walk: -34, duration: 30, delay: -1, coat: 5 },
  ];
  return (
    <g>
      {people.map((p, i) => (
        <g key={i} transform={`translate(${p.x},${p.y})`}>
          <g
            className={animPrefix ? `${animPrefix}-walk` : undefined}
            style={{ ['--walk' as string]: `${p.walk}px`, animationDuration: `${p.duration}s`, animationDelay: `${p.delay}s` }}
          >
            <rect x={-0.8} y={-3.4} width={1.6} height={3.1} rx={0.4} fill={COAT_COLOURS[p.coat]} />
            <circle cx={0} cy={-4.1} r={0.85} fill="#e6c2a3" />
          </g>
        </g>
      ))}
    </g>
  );
}

/**
 * Things that only exist after dark: street lamps along the quays, the floodlit cathedral, deck
 * lights on the two docked ferries. Rendered with the scene's `-night` class, which the scene's
 * stylesheet shows only when the page is in dark mode; the rest of the waterfront is dimmed by
 * CSS and a share of its windows switch to a warm glow (see `sceneCss` in IcebergVisual.tsx).
 */
function NightLights({ animPrefix }: { animPrefix?: string }) {
  const cls = animPrefix ? `${animPrefix}-night` : undefined;
  const lamps = [-310, -270, -150, -110, -60, -10, 40, 250, 300, 330, 380];
  return (
    <g className={cls}>
      {/* floodlight on Helsinki Cathedral: a warm haze behind it and a brighter face */}
      <ellipse cx={-118} cy={-120} rx={120} ry={95} fill="url(#hki-flood)" />
      {/* Uspenski is lit from below too */}
      <ellipse cx={198} cy={-90} rx={70} ry={60} fill="url(#hki-flood)" opacity={0.7} />
      {/* street lamps */}
      {lamps.map((x) => (
        <g key={x}>
          <line x1={x} y1={0} x2={x} y2={-9} stroke="#5b6570" strokeWidth={0.8} />
          <circle cx={x} cy={-9.5} r={1.2} fill="#ffe9a8" />
          <circle cx={x} cy={-8} r={7} fill="url(#hki-lamp)" />
        </g>
      ))}
      {/* deck lights on the docked ferries */}
      {[-42, -35, -28, -21].map((y) => (
        <rect key={y} x={-362} y={y} width={100} height={2.4} fill="#ffe3a0" opacity={0.9} />
      ))}
      {[-46, -39, -32, -25].map((y) => (
        <rect key={y} x={398} y={y} width={112} height={2.4} fill="#ffe3a0" opacity={0.9} />
      ))}
      <circle cx={-342} cy={-79} r={1.4} fill="#ff6b6b" />
      <circle cx={414} cy={-81} r={1.4} fill="#ffffff" />
      {/* the SkyWheel's rim lights */}
      <circle cx={340} cy={-46} r={36} fill="none" stroke="#9fd8ff" strokeWidth={1.4} opacity={0.9} />
      <circle cx={340} cy={-46} r={44} fill="url(#hki-wheelglow)" />
    </g>
  );
}

/**
 * The full waterfront. Render inside `<g transform="translate(centreX, waterlineY)">`.
 * Give it an `id` so the scene can reflect it in the water with a `<use>`, and an `animPrefix`
 * so the scene's stylesheet can drive the wheel and the strollers (classes `${animPrefix}-spin`
 * and `${animPrefix}-walk`).
 */
export function HelsinkiWaterfront({ id, animPrefix }: { id?: string; animPrefix?: string }) {
  return (
    <g id={id}>
      {/* far layer: the two cathedrals on their hills, softened by a little atmospheric haze */}
      <g opacity={0.94}>
        <HelsinkiCathedral />
        <UspenskiCathedral />
      </g>
      <KaivopuistoHill />
      <NeoclassicalRow />
      <EnsoGutzeitCube />
      <OldMarketHall />
      <KatajanokkaBlocks />
      <KatajanokkaTerminalAndVikingFerry />
      <OlympiaTerminalAndSiljaFerry />
      <MarketSquare />
      <AllasSeaPool />
      <SkyWheel animPrefix={animPrefix} />
      <Strollers animPrefix={animPrefix} />
      <NightLights animPrefix={animPrefix} />
    </g>
  );
}

/** Gradients the night layer needs; the scene puts these in its <defs> once. */
export function WaterfrontNightDefs() {
  return (
    <>
      <radialGradient id="hki-flood" cx="50%" cy="60%" r="50%">
        <stop offset="0%" stopColor="#ffe6b0" stopOpacity={0.55} />
        <stop offset="60%" stopColor="#ffd98a" stopOpacity={0.15} />
        <stop offset="100%" stopColor="#ffd98a" stopOpacity={0} />
      </radialGradient>
      <radialGradient id="hki-lamp" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffe9a8" stopOpacity={0.7} />
        <stop offset="100%" stopColor="#ffe9a8" stopOpacity={0} />
      </radialGradient>
      <radialGradient id="hki-wheelglow" cx="50%" cy="50%" r="50%">
        <stop offset="70%" stopColor="#9fd8ff" stopOpacity={0} />
        <stop offset="85%" stopColor="#9fd8ff" stopOpacity={0.35} />
        <stop offset="100%" stopColor="#9fd8ff" stopOpacity={0} />
      </radialGradient>
    </>
  );
}
